/**
 * All Spatie permission names used in this application.
 * Convention: gest_{resource}_{action}
 *
 * These must match exactly what is seeded in the `permissions` table.
 */
export const PERMISSIONS = {
    // Dashboard
    DASHBOARD_READ: 'gest_dashboard_read',
    MAP_READ: 'gest_resources_map_read',

    // Reports & activity log
    REPORT_READ: 'gest_reports_read',
    REPORT_EXPORT: 'gest_reports_export',

    ACTIVITY_LOG_READ: 'gest_activity_logs_read',
    ACTIVITY_LOG_EXPORT: 'gest_activity_logs_export',

    // Fleet / Resources
    RESOURCE_READ: 'gest_resources_read',
    RESOURCE_CREATE: 'gest_resources_create',
    RESOURCE_UPDATE: 'gest_resources_update',
    RESOURCE_DELETE: 'gest_resources_delete',

    // Tags
    TAG_READ: 'gest_tags_read',
    TAG_CREATE: 'gest_tags_create',
    TAG_UPDATE: 'gest_tags_update',
    TAG_DELETE: 'gest_tags_delete',

    // Tag requests
    TAG_REQUEST_READ: 'gest_tag_requests_read',
    TAG_REQUEST_CREATE: 'gest_tag_requests_create',
    TAG_REQUEST_UPDATE: 'gest_tag_requests_update',
    TAG_REQUEST_DELETE: 'gest_tag_requests_delete',

    // Incidents
    INCIDENT_READ: 'gest_occurrences_read',
    INCIDENT_CREATE: 'gest_occurrences_create',
    INCIDENT_UPDATE: 'gest_occurrences_update',
    INCIDENT_DELETE: 'gest_occurrences_delete',

    // Incident types config
    INCIDENT_TYPE_READ: 'gest_occurrence_types_read',
    INCIDENT_TYPE_CREATE: 'gest_occurrence_types_create',
    INCIDENT_TYPE_UPDATE: 'gest_occurrence_types_update',
    INCIDENT_TYPE_DELETE: 'gest_occurrence_types_delete',

    // Tickets
    TICKET_READ: 'gest_tickets_read',
    TICKET_CREATE: 'gest_tickets_create',
    TICKET_UPDATE: 'gest_tickets_update',
    TICKET_DELETE: 'gest_tickets_delete',

    // Users
    USER_READ: 'gest_users_read',
    USER_CREATE: 'gest_users_create',
    USER_UPDATE: 'gest_users_update',
    USER_DELETE: 'gest_users_delete',

    // Mass alerts
    ALERT_READ: 'gest_mass_alerts_read',
    ALERT_CREATE: 'gest_mass_alerts_create',
    ALERT_UPDATE: 'gest_mass_alerts_update',
    ALERT_DELETE: 'gest_mass_alerts_delete',

    // Contact rule groups (tags without driver)
    CONTACT_RULE_GROUP_READ: 'gest_contact_rules_groups_read',
    CONTACT_RULE_GROUP_CREATE: 'gest_contact_rules_groups_create',
    CONTACT_RULE_GROUP_UPDATE: 'gest_contact_rules_groups_update',
    CONTACT_RULE_GROUP_DELETE: 'gest_contact_rules_groups_delete',

    // Contact rules (tags without driver)
    CONTACT_RULE_READ: 'gest_contact_rules_read',
    CONTACT_RULE_CREATE: 'gest_contact_rules_create',
    CONTACT_RULE_UPDATE: 'gest_contact_rules_update',
    CONTACT_RULE_DELETE: 'gest_contact_rules_delete',

    // Driver contact rules (tags with driver)
    DRIVER_RULE_READ: 'gest_manager_contact_rules_read',
    DRIVER_RULE_CREATE: 'gest_manager_contact_rules_create',
    DRIVER_RULE_UPDATE: 'gest_manager_contact_rules_update',
    DRIVER_RULE_DELETE: 'gest_manager_contact_rules_delete',

    // External contacts
    EXTERNAL_CONTACT_READ: 'gest_external_contacts_read',
    EXTERNAL_CONTACT_CREATE: 'gest_external_contacts_create',
    EXTERNAL_CONTACT_UPDATE: 'gest_external_contacts_update',
    EXTERNAL_CONTACT_DELETE: 'gest_external_contacts_delete',

    // Categories / departments
    DEPARTMENT_READ: 'gest_departments_read',
    DEPARTMENT_CREATE: 'gest_departments_create',
    DEPARTMENT_UPDATE: 'gest_departments_update',
    DEPARTMENT_DELETE: 'gest_departments_delete',

    // Permissions management
    PERMISSION_READ: 'gest_permissions_read',
    PERMISSION_CREATE: 'gest_permissions_create',
    PERMISSION_UPDATE: 'gest_permissions_update',
    PERMISSION_DELETE: 'gest_permissions_delete',

    // Settings
    SETTINGS_READ: 'gest_settings_read',
    SETTINGS_CREATE: 'gest_settings_create',
    SETTINGS_UPDATE: 'gest_settings_update',
    SETTINGS_DELETE: 'gest_settings_delete',

    // Mass alerts
    MASS_ALERT_READ: 'gest_mass_alerts_read',
    MASS_ALERT_CREATE: 'gest_mass_alerts_create',
    MASS_ALERT_UPDATE: 'gest_mass_alerts_update',
    MASS_ALERT_DELETE: 'gest_mass_alerts_delete',
};

export const ROUTE_PERMISSIONS = {
    '/': [PERMISSIONS.DASHBOARD_READ],
    '/account': [],
    '/first-login': [],
    '/chargers': [PERMISSIONS.RESOURCE_READ],
    '/fleet': [PERMISSIONS.RESOURCE_READ],
    '/map': [PERMISSIONS.RESOURCE_READ],
    '/tags': [PERMISSIONS.TAG_READ],
    '/tickets': [PERMISSIONS.TICKET_READ],
    '/categories': [PERMISSIONS.DEPARTMENT_READ],
    '/occurrence-priorities': [PERMISSIONS.SETTINGS_READ],
    '/external-contact-types': [PERMISSIONS.EXTERNAL_CONTACT_READ],
    '/resource-statuses': [PERMISSIONS.SETTINGS_READ],
    '/tag-deactivation-reasons': [PERMISSIONS.SETTINGS_READ],
    '/mass-alert-templates': [PERMISSIONS.MASS_ALERT_READ],
    '/incidents': [PERMISSIONS.INCIDENT_READ],
    '/users': [PERMISSIONS.USER_READ],
    '/permissions': [PERMISSIONS.PERMISSION_READ],
    '/settings': [PERMISSIONS.SETTINGS_READ],
    '/incident-types': [PERMISSIONS.INCIDENT_TYPE_READ],
    '/mass-alerts': [PERMISSIONS.MASS_ALERT_READ],
    '/activity-log': [PERMISSIONS.ACTIVITY_LOG_READ],
    '/reports': [PERMISSIONS.REPORT_READ],
    '/contact-rules': [PERMISSIONS.CONTACT_RULE_GROUP_READ, PERMISSIONS.CONTACT_RULE_READ],
    '/driver-contact-rules': [PERMISSIONS.DRIVER_RULE_READ],
    '/external-contacts': [PERMISSIONS.EXTERNAL_CONTACT_READ],
};

export const hasDirectPermissions = (userPermissions = [], requiredPermissions) => {
    if (!Array.isArray(requiredPermissions)) return false;
    if (requiredPermissions.length === 0) return true;
    return requiredPermissions.every(permission => userPermissions.includes(permission));
};

const routePermissionKey = (path) => {
    if (!path) return path;
    if (ROUTE_PERMISSIONS[path]) return path;
    if (path.startsWith('/tags/')) return '/tags';
    return path;
};

export const canAccessRoute = (path, userPermissions = []) => {
    const required = ROUTE_PERMISSIONS[routePermissionKey(path)];
    if (!Array.isArray(required)) return false;
    return hasDirectPermissions(userPermissions, required);
};

/**
 * Default permissions granted per Spatie role.
 * Used for seeding and for the UI permission picker defaults.
 */
export const DEFAULT_PERMISSIONS_BY_ROLE = {
    admin: [],
    gestor: [],
    condutor: [],
    manutencao: [],
    vigilante: [],
};
